import {ChangeDetectorRef, Component, ElementRef, Input, ViewChild} from '@angular/core';
import {INewsPost} from '@api/models/news/news';
import {Event} from '@api/models/event/event';
import {NewsSectionLayout} from '@lib-modules/section/components/news-section/news-section.component';
import {ColorScheme} from '@lib/models/color-scheme/color-scheme';
import {SectionHeaderTitleSize} from '@lib-modules/section/components/section-header/section-header.component';
import {LinkService} from '@core/services/link.service';
import {PickerItem, PickerItemType} from '@lib-modules/picker/components/picker-item/picker-item.component';
import {MatchSectionLayout} from '../match-section/match-section.component';
import {League} from '@api/models/league/league';
import {LeagueService} from '@core/services/league.service';
import {EventCardService, EventContentType} from '@lib-modules/cards/services/event-card/event-card.service';
import {SportTypeService} from '@api/services/sport-type/sport-type.service';
import {SportType} from '@lib/models/sport-type/sport-type';
import {NewsCardService, NewsContentType} from '@lib-modules/cards/services/news-card/news-card.service';
import {VideoCardService, VideoContentType} from '@lib-modules/cards/services/video-card/video-card.service';

export enum FilterSectionLayout {
  News = 'news',
  Video = 'video',
  Overview = 'overview'
}

@Component({
  selector: 'lib-filter-section',
  templateUrl: './filter-section.component.html',
  styleUrls: ['./filter-section.component.scss'],
})
export class FilterSectionComponent {

  @ViewChild('videosSection') videosSection: ElementRef<HTMLDivElement>;
  @ViewChild('newsSection') newsSection: ElementRef<HTMLDivElement>;
  @ViewChild('overviewSection') overviewSection: ElementRef<HTMLDivElement>;
  @Input() filterItems: PickerItem[];
  @Input() newsType = NewsContentType.VideoNews;
  @Input() layout = FilterSectionLayout.News;
  @Input() title: string;
  @Input() titleIcon: string;
  @Input() titleIconUrl: string;
  @Input() colorScheme = ColorScheme.Dark;

  public live: Event[] = [];
  public replays: Event[] = [];
  public highlights: Event[] = [];
  public pastEvents: Event[] = [];
  public upcomingEvents: Event[] = [];
  public news: INewsPost[] = [];
  public otherNews: INewsPost[] = [];

  public leagueForTable: League;
  public tableTeamId?: string;
  public loading = false;
  public moreButtonLink?: string;
  public moreButtonTitle?: string;
  public mixedLayout = NewsSectionLayout.Mixed;
  public rowLayout = NewsSectionLayout.Row;
  public smallTitleSize = SectionHeaderTitleSize.Medium;

  protected readonly ColorScheme = ColorScheme;
  protected readonly FilterSectionLayout = FilterSectionLayout;
  protected readonly MatchSectionLayout = MatchSectionLayout;

  constructor(public leagueService: LeagueService,
              private eventCardService: EventCardService,
              private newsCardService: NewsCardService,
              private videoCardService: VideoCardService,
              private cdr: ChangeDetectorRef) {
  }

  public async onItemSelect(selectedItem: PickerItem) {
    this.showSubSection(false);
    // TODO: Find a better approach to fade out subsections
    setTimeout(async () => {
      this.loading = true;
      try {
        switch (this.layout) {
          case FilterSectionLayout.News:
            await this.onNewsSelect(selectedItem);
            break
          case FilterSectionLayout.Video:
            await this.onVideoSelect(selectedItem);
            break;
          case FilterSectionLayout.Overview:
            await this.setOverviewData(selectedItem);
            break;
        }
        this.loading = false;
        setTimeout(() => {
          this.showSubSection(true);
          this.cdr.detectChanges();
        }, 150);
      } catch (e) {
        console.error('Error handling item select!');
        throw e;
      }
    }, 150);
  }

  private async setOverviewData(selectedItem: PickerItem): Promise<void> {
    switch (selectedItem.type) {
      case PickerItemType.League:
        this.leagueForTable = await this.leagueService.getLeague(selectedItem.objectId);
        this.tableTeamId = null;
        this.setMoreButton('Zur Liga', LinkService.getLeagueLink(selectedItem.readableId));
        break;

      case PickerItemType.Team:
        this.leagueForTable = await this.leagueService.getLeagueOfTeam(selectedItem.objectId);
        this.tableTeamId = selectedItem.objectId;
        this.setMoreButton('Zum Team', LinkService.getTeamLink(selectedItem.readableId));
        break;

      case PickerItemType.State:
        this.news = await this.newsCardService.fetchData(NewsContentType.EntityNews, selectedItem);
        this.otherNews = await this.newsCardService.fetchData(NewsContentType.VideoNews, selectedItem);
        return;
    }
    this.upcomingEvents = await this.eventCardService.fetchData(EventContentType.UpcomingEvents, selectedItem)
    this.pastEvents = await this.eventCardService.fetchData(EventContentType.PastEvents, selectedItem)
    this.news = await this.newsCardService.fetchData(this.newsType, selectedItem)
  }

  private async onNewsSelect(selectedItem: PickerItem) {
    const allNews = await this.newsCardService.fetchData(NewsContentType.EntityNews, selectedItem, 0, 16);
    this.news = allNews.slice(0, 4);
    this.otherNews = allNews.slice(4, 16);
  }


  private async onVideoSelect(selectedItem: PickerItem) {

    this.live = await this.videoCardService.fetchData(VideoContentType.ActiveStreams, selectedItem)
    this.replays = await this.videoCardService.fetchData(VideoContentType.Replay, selectedItem)

    if (SportTypeService.selectedSportType === SportType.Football) {
      this.news = await this.newsCardService.fetchData(NewsContentType.VideoNews, selectedItem)
      switch (selectedItem.type) {
        case PickerItemType.Team:
          this.setMoreButton('Alle Team-Videos', LinkService.getTeamLink(selectedItem.readableId) + '/videos');
          break;
        case PickerItemType.League:
          this.setMoreButton('Alle Liga-Videos', LinkService.getLeagueLink(selectedItem.readableId) + '/videos');
          break;
        case 'all':
          break;
      }
    } else {
      this.highlights = await  this.videoCardService.fetchData(VideoContentType.Highlights, selectedItem);
    }
  }

  private setMoreButton(title: string, link: string) {
    this.moreButtonTitle = title;
    this.moreButtonLink = link;
  }

  get subsectionColorScheme(): ColorScheme {
    switch (this.colorScheme) {
      case ColorScheme.Dark:
        return ColorScheme.DarkBlue;
      case ColorScheme.LightGreen:
        return ColorScheme.Light;
    }
    // TODO: adapt for other color schemes
    return this.colorScheme;
  }

  private showSubSection(show: boolean) {
    let subSection: ElementRef<HTMLDivElement>;
    switch (this.layout) {
      case FilterSectionLayout.Video:
        subSection = this.videosSection;
        break;
      case FilterSectionLayout.News:
        subSection = this.newsSection;
        break;
      case FilterSectionLayout.Overview:
        subSection = this.overviewSection;
        break;
    }

    if (subSection && subSection.nativeElement) {
      if (show) {
        subSection.nativeElement.classList.add('fade-in');
      } else {
        subSection.nativeElement.classList.remove('fade-in');
      }
    }
  }
}
